import { HOURS_PER_DAY } from '../../constants/time';
import type { DateIso, Phase } from '../../types/domain';
import { currentContractYear, workingDaysInPeriod } from '../dates';
import { toNumber } from '../numbers';
import type { HoursByDayAndDev } from '../planning/types';
import { isPhaseOpen } from './activePhases';

const REGIE = ['regie_temporelle', 'regie_horaire'];
const round1 = (n: number) => Math.round(n * 10) / 10;

/** Heures saisies entre deux dates incluses. */
export function hoursBetween(
  daily: HoursByDayAndDev | null | undefined,
  from: DateIso,
  to: DateIso,
) {
  let total = 0;
  for (const [date, byDev] of Object.entries(daily || {})) {
    if (date < from || date > to) continue;
    for (const h of Object.values(byDev)) total += h;
  }
  return total;
}

export interface RegieTimeSpent {
  monthDays: number;
  yearDays: number;
  yearCapacity: number;
  yearFrom: DateIso | null;
  yearTo: DateIso | null;
  pct: number | null;
}

/* Carte d'un client en régie (arbitrage du 07/10/2026) : jours saisis en feuilles d'heures depuis
   le 1er du mois, et sur l'année de contrat en cours (mois anniversaires depuis le début de la
   phase) face à la capacité de cette année (devs × jours ouvrés). Toutes les régies ouvertes du
   client sont cumulées (Lahaye : Be-Modal, Lahaye Mobile et Galactic sur une seule carte). */
export function regieTimeSpent(
  phases: Phase[],
  dailyByPhase: Record<string, HoursByDayAndDev>,
  todayIso: DateIso,
): RegieTimeSpent {
  const monthStart = `${todayIso.slice(0, 7)}-01`;
  let monthHours = 0;
  let yearHours = 0;
  let yearCapacity = 0;
  const years: { from: DateIso; to: DateIso }[] = [];
  for (const ph of phases) {
    if (!REGIE.includes(ph.facturation || '') || !isPhaseOpen(ph, todayIso)) continue;
    const daily = dailyByPhase[ph.id];
    // Bornée aux dates de la phase : une saisie hors de sa fenêtre ne compte pas (comme le suivi).
    const from = ph.dateDebut && ph.dateDebut > monthStart ? ph.dateDebut : monthStart;
    monthHours += hoursBetween(daily, from, todayIso);
    const year = currentContractYear(ph.dateDebut, ph.dateFin, todayIso);
    if (!year) continue;
    years.push(year);
    yearHours += hoursBetween(daily, year.from, todayIso < year.to ? todayIso : year.to);
    yearCapacity += toNumber(ph.nbDevs || 0) * workingDaysInPeriod(year.from, year.to);
  }
  // Libellé d'année seulement si toutes les régies cumulées partagent la même année de contrat.
  const same =
    years.length > 0 && years.every((y) => y.from === years[0].from && y.to === years[0].to);
  const yearDays = round1(yearHours / HOURS_PER_DAY);
  const capacity = round1(yearCapacity);
  return {
    monthDays: round1(monthHours / HOURS_PER_DAY),
    yearDays,
    yearCapacity: capacity,
    yearFrom: same ? years[0].from : null,
    yearTo: same ? years[0].to : null,
    pct: capacity > 0 ? Math.round((yearDays / capacity) * 100) : null,
  };
}
